import { FC } from 'react';
import { useFavoritesEnabled } from '../../../utils/useFavoritesEnabled';
import { DashboardPage } from '../../dashboard/DashboardPage';
import { useDialogScroll } from '../../feedback/hooks/useDialogScroll';
import { IconClose } from '../../icon/IconClose';
import { IconButton } from '../../input/IconButton';
import { Box } from '../../layout/Box';
import { DataTestId } from '../../layout/DataTestIdWrapper';
import { Flex } from '../../layout/Flex';
import { SaveToCollectionContent } from '../SaveToCollectionDrawer/parts/SaveToCollectionContent/SaveToCollectionContent';
import {
    SaveToCollectionFooter,
    shouldShowSaveFooter,
} from '../SaveToCollectionDrawer/parts/SaveToCollectionFooter/SaveToCollectionFooter';
import { TitleContent } from '../SaveToCollectionDrawer/parts/TitleContent/TitleContent';
import { SaveErrorContent } from './parts/SaveErrorContent/SaveErrorContent';
import { SaveSuccessContent } from './parts/SaveSuccessContent/SaveSuccessContent';
import { SaveToCollectionPageProps } from './types';

export const SaveToCollectionPage: FC<SaveToCollectionPageProps> = ({
    collections,
    isCollectionsLoading,
    onSaveToExistentCollection,
    values,
    handlers,
    step,
    isNotRegistered,
    onAddClick,
    isSaved,
    isError,
    collectionName,
    onClose,
    loadMore,
    isLoadingMore,
}) => {
    const isFavoritesEnabled = useFavoritesEnabled();
    const { modalContentRef, atTop } = useDialogScroll();

    const showFooter = shouldShowSaveFooter(step, isFavoritesEnabled);

    const header = (
        <Flex
            px={2}
            py={2}
            width="100%"
            position="sticky"
            top={0}
            zIndex={1}
            alignItems="center"
            direction="row"
            background="var(--background-primary)"
            borderBottom={atTop ? '' : '1px solid var(--divider)'}
        >
            <TitleContent step={step} />
            <Box position="absolute" right={1} top="50%" transform="translateY(-50%)">
                <IconButton onClick={onClose} dataTestId={DataTestId.DIALOG_CLOSE_BUTTON}>
                    <IconClose />
                </IconButton>
            </Box>
        </Flex>
    );

    if (isError) {
        return (
            <DashboardPage
                hideHeader
                minHeight={{ xs: '100dvh', sm: '100vh' }}
                height={{ xs: '100dvh', sm: '100vh' }}
                backgroundColor="var(--background-primary)"
                dataTestId={DataTestId.SAVE_TO_COLLECTION_DRAWER}
            >
                <Flex direction="column" width="100%" height="100%">
                    {header}
                    <SaveErrorContent />
                </Flex>
            </DashboardPage>
        );
    }

    if (isSaved) {
        return (
            <DashboardPage
                hideHeader
                minHeight={{ xs: '100dvh', sm: '100vh' }}
                height={{ xs: '100dvh', sm: '100vh' }}
                backgroundColor="var(--background-primary)"
                dataTestId={DataTestId.SAVE_TO_COLLECTION_DRAWER}
            >
                <Flex direction="column" width="100%" height="100%">
                    {header}
                    <SaveSuccessContent collectionName={collectionName} />
                </Flex>
            </DashboardPage>
        );
    }

    return (
        <DashboardPage
            hideHeader
            minHeight={{ xs: '100dvh', sm: '100vh' }}
            height={{ xs: '100dvh', sm: '100vh' }}
            backgroundColor="var(--background-primary)"
            dataTestId={DataTestId.SAVE_TO_COLLECTION_DRAWER}
        >
            <Flex direction="column" width="100%" height="100%">
                {header}

                <Flex
                    ref={modalContentRef}
                    direction="column"
                    flex={1}
                    width="100%"
                    overflowY="auto"
                >
                    <SaveToCollectionContent
                        step={step}
                        collections={collections}
                        isCollectionsLoading={isCollectionsLoading}
                        onSaveToExistentCollection={onSaveToExistentCollection}
                        onAddClick={onAddClick}
                        onClose={onClose}
                        values={values}
                        handlers={handlers}
                        isFavoritesEnabled={isFavoritesEnabled}
                        loadMore={loadMore}
                        isLoadingMore={isLoadingMore}
                    />
                </Flex>

                {showFooter ? (
                    <Box
                        px={2}
                        py={2}
                        width="100%"
                        borderTop="1px solid var(--divider)"
                        background="var(--background-primary)"
                    >
                        <SaveToCollectionFooter
                            step={step}
                            onAddClick={onAddClick}
                            onSubmit={handlers.onSubmit}
                            values={values}
                            isNotRegistered={isNotRegistered}
                        />
                    </Box>
                ) : null}
            </Flex>
        </DashboardPage>
    );
};
